import { db } from './db';
import { mustDeferSend, sendTenantOf } from './client-id';
import type { PersonnelPositionBuffer } from './db';
import {
  flushPositions,
  uploadPositionsInChunks,
} from '@/services/personnel-tracking-flush.service';
import { networkService } from '@/services/network.service';
import type { PositionRecord } from '@/services/personnel-tracking-flush.service';

/**
 * Offline sync service for personnel tracking positions.
 *
 * When the store's flush() fails (e.g., device is offline), positions are
 * saved to the IndexedDB `personnelPositionBuffer` table. This service
 * handles syncing those buffered positions back to the server when
 * connectivity is restored.
 *
 * P0-15:
 *  - Every point is uploaded with its point_id. The row's primary key IS the
 *    point_id, so the key is persisted with the point and is identical on
 *    every retry. The server stores a replayed point once.
 *  - Uploads are chunked to at most 500 points, the server's per-request
 *    cap. Before this, a backlog over 500 was re-sent whole, refused with a
 *    400 every time, and never left the device.
 *  - A permanent 4xx dead-letters the chunk: its rows are flagged
 *    `dead_letter` and skipped from then on, but kept. 401, 403, 408, 429,
 *    5xx and network errors are transient and are retried.
 *
 * Ported from the gps-track offline service pattern.
 */

/** Map a buffered row to the upload shape, backfilling point_id from the row id. */
function toPositionRecord(row: PersonnelPositionBuffer): PositionRecord {
  return {
    // Rows written before P0-15 have no point_id. Their row id is a random
    // UUID that is stable for the life of the row, so it is a valid key.
    point_id: row.point_id ?? row.id,
    lat: row.lat,
    lng: row.lng,
    altitude: row.altitude,
    accuracy: row.accuracy,
    speed: row.speed,
    heading: row.heading,
    battery_percent: row.battery_percent,
    recorded_at: row.recorded_at,
  };
}

/** Build buffer rows whose primary key is the point's own point_id. */
function toBufferRows(
  sessionId: string,
  points: PositionRecord[],
  extra: Partial<PersonnelPositionBuffer> = {}
): PersonnelPositionBuffer[] {
  return points.map((p) => {
    const pointId = p.point_id ?? crypto.randomUUID();
    return {
      id: pointId,
      point_id: pointId,
      session_id: sessionId,
      lat: p.lat,
      lng: p.lng,
      altitude: p.altitude,
      accuracy: p.accuracy,
      speed: p.speed,
      heading: p.heading,
      battery_percent: p.battery_percent,
      recorded_at: p.recorded_at,
      is_synced: false,
      created_at: Date.now(),
      ...extra,
    };
  });
}

export interface PersonnelPositionSyncResult {
  synced: number;
  failed: number;
  /** Points the server refused for good during this run (set aside, not retried). */
  deadLettered: number;
  /** Surfaced by the sync orchestrator; only permanent rejections are reported. */
  errors: string[];
}

export const personnelPositionSyncService = {
  /**
   * Save positions to IndexedDB for later sync.
   * Called by the store's flush() action on network failure.
   *
   * bulkPut keyed by point_id makes this idempotent: saving the same point
   * twice keeps one row instead of throwing on the duplicate key.
   */
  async savePointsOffline(
    sessionId: string,
    points: PositionRecord[]
  ): Promise<void> {
    await db.personnelPositionBuffer.bulkPut(toBufferRows(sessionId, points));
  },

  /**
   * Keep points the server refused with a permanent 4xx, flagged so syncAll()
   * never resends them. They still count as unsynced (not on the server), so
   * the sync indicator and the Discard dialog both see them.
   */
  async saveDeadLetteredPoints(
    sessionId: string,
    points: PositionRecord[],
    status: number | undefined
  ): Promise<void> {
    await db.personnelPositionBuffer.bulkPut(
      toBufferRows(sessionId, points, {
        dead_letter: true,
        dead_letter_status: status,
        dead_lettered_at: Date.now(),
      })
    );
  },

  /**
   * Sync all unsynced, non-dead-lettered positions to the server.
   * Groups by session_id, uploads each session oldest-first in chunks of at
   * most 500, and marks each chunk synced once the server has answered for it.
   */
  async syncAll(): Promise<PersonnelPositionSyncResult> {
    const result: PersonnelPositionSyncResult = { synced: 0, failed: 0, deadLettered: 0, errors: [] };

    if (!networkService.isOnline()) return result;

    // Another person's buffered points are never sent under this credential (the server would
    // refuse them as SESSION_NOT_OWNED and they would be dead-lettered): they wait for their
    // owner, or for the handoff, which carries their origin (S-06).
    const unsyncedPoints = await db.personnelPositionBuffer
      .filter((p) => !p.is_synced && !p.dead_letter && !mustDeferSend(p as never))
      .toArray();

    if (unsyncedPoints.length === 0) return result;

    // Group by session_id
    const grouped = new Map<string, PersonnelPositionBuffer[]>();
    for (const pt of unsyncedPoints) {
      const existing = grouped.get(pt.session_id) || [];
      existing.push(pt);
      grouped.set(pt.session_id, existing);
    }

    for (const [sessionId, pts] of grouped) {
      // Primary-key order is random (UUIDs). Send oldest first so each chunk
      // moves the session's last position forward.
      const rows = [...pts].sort((a, b) => a.recorded_at.localeCompare(b.recorded_at));
      const byPointId = new Map(rows.map((r) => [r.point_id ?? r.id, r] as const));

      const markRows = async (records: PositionRecord[], changes: Partial<PersonnelPositionBuffer>) => {
        const ids = records
          .map((rec) => byPointId.get(rec.point_id as string)?.id)
          .filter((id): id is string => Boolean(id));
        if (ids.length > 0) {
          await db.personnelPositionBuffer.where('id').anyOf(ids).modify(changes);
        }
      };

      try {
        // Sent under the rows' own tenant (round 7 L1).
        const pin = sendTenantOf(rows[0]);
        const outcome = await uploadPositionsInChunks(sessionId, rows.map(toPositionRecord), (sid, chunk) => flushPositions(sid, chunk, pin));

        await markRows(outcome.delivered, { is_synced: true });
        result.synced += outcome.delivered.length;
        result.failed += outcome.retry.length;

        for (const dead of outcome.deadLettered) {
          await markRows(dead.points, {
            dead_letter: true,
            dead_letter_status: dead.status,
            dead_lettered_at: Date.now(),
          });
          result.deadLettered += dead.points.length;
          result.errors.push(
            `Personnel positions: server refused ${dead.points.length} point(s)` +
              ` (HTTP ${dead.status ?? 'error'}); kept on this device and not retried`
          );
        }
      } catch {
        // IndexedDB failure while marking: leave the rows unsynced. They are
        // resent next time with the same point_ids, and the server dedupes them.
        result.failed += rows.length;
      }
    }

    return result;
  },

  /**
   * Count unsynced records in the buffer. This includes dead-lettered points:
   * they are not on the server, and offlineResetService's Discard deletes
   * them along with every other unsynced row, so the count must show them.
   */
  async getPendingCount(): Promise<number> {
    return db.personnelPositionBuffer.filter((p) => !p.is_synced).count();
  },

  /** Count points the server refused for good. They are never resent. */
  async getDeadLetterCount(): Promise<number> {
    return db.personnelPositionBuffer.filter((p) => !p.is_synced && !!p.dead_letter).count();
  },

  /**
   * Delete records that have already been synced to free up space.
   */
  async clearSyncedData(): Promise<void> {
    await db.personnelPositionBuffer.filter((p) => p.is_synced).delete();
  },
};
